import Phaser from 'phaser';
import type { PlayerState } from '@/models/types';
import { getBuildingsByEra } from '@/models/constants';
import { ResourceManager } from '@/systems/ResourceManager';
import { BuildingManager } from '@/systems/BuildingManager';
import { GameLoop } from '@/systems/GameLoop';
import { SaveManager } from '@/systems/SaveManager';
import { PrestigeManager } from '@/systems/PrestigeManager';
import { RaidManager } from '@/systems/RaidManager';
import { RaidService } from '@/systems/RaidService';
import { TalentManager } from '@/systems/TalentManager';
import { CosmeticsManager } from '@/systems/CosmeticsManager';
import { BattlePassManager } from '@/systems/BattlePassManager';
import { XPManager } from '@/systems/XPManager';
import { DailyShopManager } from '@/systems/DailyShopManager';
import { HUD } from '@/ui/HUD';
import { BuildingPanel } from '@/ui/BuildingPanel';
import { PrestigePanel } from '@/ui/PrestigePanel';
import { RaidPanel } from '@/ui/RaidPanel';
import { LeaderboardPanel } from '@/ui/LeaderboardPanel';
import { ShopPanel } from '@/ui/ShopPanel';
import { TalentPanel } from '@/ui/TalentPanel';
import { ProfilePanel } from '@/ui/ProfilePanel';
import { CosmeticsPanel } from '@/ui/CosmeticsPanel';
import { BattlePassPanel } from '@/ui/BattlePassPanel';
import { BuildingVisuals } from '@/scenes/BuildingVisuals';
import { Tutorial } from '@/ui/Tutorial';
import { EventManager } from '@/systems/EventManager';
import { EventPopup } from '@/ui/EventPopup';
import { AchievementManager } from '@/systems/AchievementManager';
import { AchievementPanel } from '@/ui/AchievementPanel';
import { AchievementToast } from '@/ui/AchievementToast';
import { Router } from '@/systems/Router';
import { SYNERGIES_ANCIENT } from '@/models/synergies';
import { getSynergiesByEra } from '@/models/era-synergies';
import { ChallengeManager } from '@/systems/ChallengeManager';
import { ChallengePanel } from '@/ui/ChallengePanel';
import { SeasonManager } from '@/systems/SeasonManager';
import { SeasonPanel } from '@/ui/SeasonPanel';

export class KingdomScene extends Phaser.Scene {
  private resources!: ResourceManager;
  private buildings!: BuildingManager;
  private gameLoop!: GameLoop;
  private saveManager!: SaveManager;
  private prestige!: PrestigeManager;
  private raidManager!: RaidManager;
  private raidService!: RaidService;
  private hud!: HUD;
  private buildingPanel!: BuildingPanel;
  private prestigePanel!: PrestigePanel;
  private raidPanel!: RaidPanel;
  private leaderboardPanel!: LeaderboardPanel;
  private shopPanel!: ShopPanel;
  private buildingVisuals!: BuildingVisuals;
  private tutorial: Tutorial | null = null;
  private isNewPlayer: boolean = false;
  private tickAccumulator: number = 0;
  private uiUpdateAccumulator: number = 0;
  private gems: number = 0;
  private streakCount: number = 0;
  private eventManager!: EventManager;
  private eventPopup!: EventPopup;
  private achievements!: AchievementManager;
  private achievementPanel!: AchievementPanel;
  private achievementToast!: AchievementToast;
  private talents!: TalentManager;
  private talentPanel!: TalentPanel;
  private profilePanel!: ProfilePanel;
  private router!: Router;
  private cosmetics!: CosmeticsManager;
  private cosmeticsPanel!: CosmeticsPanel;
  private battlePass!: BattlePassManager;
  private battlePassPanel!: BattlePassPanel;
  private xpManager!: XPManager;
  private dailyShop!: DailyShopManager;
  private upgradeCounter: number = 0;
  private challenges!: ChallengeManager;
  private challengePanel!: ChallengePanel;
  private seasonManager!: SeasonManager;
  private seasonPanel!: SeasonPanel;

  constructor() {
    super({ key: 'Kingdom' });
  }

  private static getEraSynergies(eraId: string): readonly import('@/models/synergies').Synergy[] {
    const synergies = getSynergiesByEra(eraId);
    return synergies.length > 0 ? synergies : SYNERGIES_ANCIENT;
  }

  init(data: { userId: string; savedState: PlayerState | null }): void {
    this.resources = new ResourceManager();
    const era = data.savedState?.era ?? 'ancient';
    const eraBuildings = getBuildingsByEra(era);
    this.buildings = new BuildingManager(eraBuildings);
    this.buildings.setSynergies(KingdomScene.getEraSynergies(era));
    this.saveManager = new SaveManager();
    this.saveManager.setPlayerId(data.userId);
    this.prestige = new PrestigeManager();
    this.raidManager = new RaidManager();
    this.raidService = new RaidService();
    this.talents = new TalentManager();

    if (data.savedState) {
      this.resources.setAll(data.savedState.resources);
      this.buildings.setLevels(data.savedState.buildings);
      this.gems = data.savedState.gems ?? 0;
      this.streakCount = data.savedState.streakCount ?? 0;
      this.prestige.restoreState(
        data.savedState.era,
        data.savedState.prestigeCount,
        data.savedState.prestigeMultiplier,
      );
      this.talents.restoreState(
        data.savedState.arcaneShards ?? 0,
        data.savedState.talentLevels ?? {},
      );
      this.achievements = new AchievementManager();
      this.achievements.restoreState(
        data.savedState.achievementCounters ?? {},
        data.savedState.unlockedAchievements ?? [],
      );
      this.trackAchievement('total_logins', 1);

      const offlineSeconds = (Date.now() - data.savedState.lastTick) / 1000;
      this.gameLoop = new GameLoop(this.resources, this.buildings, this.prestige.getPrestigeMultiplier());
      if (offlineSeconds > 5) {
        this.gameLoop.applyOfflineEarnings(offlineSeconds);
      }
    } else {
      this.resources.add({ gold: 50, food: 50, faith: 20, knowledge: 10 });
      const extraGold = this.talents.getTalentEffect('starting_gold');
      const extraFood = this.talents.getTalentEffect('starting_food');
      if (extraGold > 0 || extraFood > 0) {
        this.resources.add({ gold: extraGold, food: extraFood, faith: 0, knowledge: 0 });
      }
      this.gameLoop = new GameLoop(this.resources, this.buildings, this.prestige.getPrestigeMultiplier());
      this.isNewPlayer = true;
      this.trackAchievement('total_logins', 1);
    }

    this.cosmetics = new CosmeticsManager();
    this.battlePass = new BattlePassManager();
    this.xpManager = new XPManager();
    this.dailyShop = new DailyShopManager();
    this.challenges = new ChallengeManager();
    this.challenges.setUserId(data.userId);
    this.seasonManager = new SeasonManager();

    if (data.savedState) {
      this.cosmetics.restoreState(
        data.savedState.cosmeticsOwned ?? [],
        data.savedState.cosmeticsEquipped ?? { frame: 'default', badge: 'none', name_color: '#c9a44c' },
      );
      this.battlePass.restoreState(data.savedState.battlePass ?? null);
    }

    this.eventManager = new EventManager();
    this.gameLoop.setEventManager(this.eventManager);
    this.gameLoop.setTalentManager(this.talents);
    this.applyTalentBonuses();
    if (!this.achievements) {
      this.achievements = new AchievementManager();
    }
  }

  private applyTalentBonuses(): void {
    this.buildings.setTalentBonuses(
      this.talents.getTalentEffect('cost'),
      this.talents.getTalentEffect('synergy'),
    );
    this.eventManager.setSpawnSpeedReduction(this.talents.getTalentEffect('event_speed'));
    const extraRaids = Math.floor(this.talents.getTalentEffect('raid_count'));
    if (extraRaids > 0) {
      this.raidManager.setRaidsRemaining(this.raidManager.getRaidsRemaining() + extraRaids);
    }
  }

  create(): void {
    const { width, height } = this.scale;

    // Background gradient
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x0a0a1a, 0x0a0a1a, 0x1a1035, 0x1a1035);
    bg.fillRect(0, 0, width, height);

    // Decorative stars
    for (let i = 0; i < 50; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height * 0.6;
      const alpha = 0.1 + Math.random() * 0.4;
      const size = 1 + Math.random() * 2;
      this.add.circle(x, y, size, 0xc9a44c, alpha);
    }

    // Title with nicer font
    this.add
      .text(width / 2, height * 0.35, 'ARCHON', {
        fontSize: '36px', color: '#c9a44c', fontFamily: 'Cinzel, Georgia, serif',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setShadow(0, 0, '#c9a44c', 8, false, true);

    this.add
      .text(width / 2, height * 0.35 + 40, 'Eternal Throne', {
        fontSize: '14px', color: '#6b6570', fontFamily: 'Cinzel, Georgia, serif',
      })
      .setOrigin(0.5);

    // Building visuals on canvas
    const currentBuildings = this.buildings.getBuildings();
    const buildingIds = currentBuildings.map(b => b.id);
    this.buildingVisuals = new BuildingVisuals(this, buildingIds, width / 2, height * 0.5);

    const overlay = document.getElementById('ui-overlay')!;
    this.hud = new HUD(overlay);
    this.buildingPanel = new BuildingPanel(overlay, currentBuildings, (buildingId) => {
      this.buildings.upgrade(buildingId, this.resources);
      this.tutorial?.notifyUpgrade();
      this.trackAchievement('buildings_upgraded', 1);
    });

    this.prestigePanel = new PrestigePanel(overlay, () => {
      const baseShardsEarned = this.prestige.prestige(this.resources, this.buildings);
      if (baseShardsEarned > 0) {
        // Use TalentManager as authoritative shard formula (includes prestige_bonus)
        const totalProd = this.buildings.getTotalProductionRate();
        const prestigeBonus = this.talents.getTalentEffect('prestige_bonus');
        const totalShards = TalentManager.calculateShardsForPrestige(totalProd, prestigeBonus);
        this.talents.addShards(totalShards > 0 ? totalShards : baseShardsEarned);

        // Rebuild for new era
        const newEra = this.prestige.getCurrentEra().id;
        const newBuildings = getBuildingsByEra(newEra);
        this.buildings = new BuildingManager(newBuildings);
        this.buildings.setSynergies(KingdomScene.getEraSynergies(newEra));
        this.gameLoop = new GameLoop(this.resources, this.buildings, this.prestige.getPrestigeMultiplier());
        this.gameLoop.setEventManager(this.eventManager);
        this.gameLoop.setTalentManager(this.talents);
        this.applyTalentBonuses();

        // Apply starting resources from talents
        const extraGold = this.talents.getTalentEffect('starting_gold');
        const extraFood = this.talents.getTalentEffect('starting_food');
        if (extraGold > 0 || extraFood > 0) {
          this.resources.add({ gold: extraGold, food: extraFood, faith: 0, knowledge: 0 });
        }

        this.gameLoop.setPrestigeMultiplier(this.prestige.getPrestigeMultiplier());
        this.prestigePanel.hide();
        this.trackAchievement('prestiges', 1);
        this.battlePass.addXP(this.xpManager.getXPForAction('prestige'));

        // Rebuild building panel + visuals for new era
        this.buildingPanel.destroy();
        this.buildingPanel = new BuildingPanel(overlay, newBuildings, (buildingId) => {
          this.buildings.upgrade(buildingId, this.resources);
          this.tutorial?.notifyUpgrade();
          this.trackAchievement('buildings_upgraded', 1);
        });

        const newBuildingIds = newBuildings.map(b => b.id);
        this.buildingVisuals.destroy();
        this.buildingVisuals = new BuildingVisuals(this, newBuildingIds, width / 2, height * 0.5);
      }
    });

    // Nav buttons
    const prestigeBtn = document.createElement('button');
    prestigeBtn.className = 'btn btn-nav';
    prestigeBtn.style.top = '100px';
    prestigeBtn.textContent = '⬆ Prestige';
    prestigeBtn.addEventListener('click', () => this.prestigePanel.toggle());
    overlay.appendChild(prestigeBtn);

    // Raid panel + button
    this.raidPanel = new RaidPanel(
      overlay,
      async () => {
        this.raidPanel.setStatus('Searching...');
        const power = RaidManager.calculateDefensePower(this.buildings.getLevels());
        const targets = await this.raidService.getTargets(this.prestige.getCurrentEra().id, power);
        this.raidPanel.setTargets(targets, this.resources.get('food'));
        this.raidPanel.setStatus(targets.length > 0 ? '' : 'No targets in your era');
      },
      async (targetId, troops) => {
        if (!this.raidManager.canRaid()) {
          this.raidPanel.setStatus('No raids remaining');
          return;
        }
        this.raidPanel.setStatus('Raiding...');
        const result = await this.raidService.executeRaid(targetId, troops);
        if (!result) {
          this.raidPanel.setStatus('Raid failed');
          return;
        }
        this.raidManager.useRaid();
        this.resources.subtract({ gold: 0, food: troops, faith: 0, knowledge: 0 });
        if (result.outcome === 'victory') {
          this.resources.add({ gold: result.loot.gold, food: result.loot.food, faith: 0, knowledge: 0 });
          this.raidPanel.setStatus(`Victory! +${result.loot.gold} Au +${result.loot.food} Fd`);
          this.trackAchievement('raids_won', 1);
          this.battlePass.addXP(this.xpManager.getXPForAction('raid_victory'));
        } else {
          this.raidPanel.setStatus(`Defeat. ${troops} food lost.`);
        }
        this.raidPanel.updateRaidsRemaining(this.raidManager);
      },
    );

    const raidBtn = document.createElement('button');
    raidBtn.className = 'btn btn-nav raid';
    raidBtn.style.top = '138px';
    raidBtn.textContent = '⚔️ Raids';
    raidBtn.addEventListener('click', () => {
      this.raidPanel.toggle();
      this.raidPanel.updateRaidsRemaining(this.raidManager);
    });
    overlay.appendChild(raidBtn);

    // Leaderboard
    this.leaderboardPanel = new LeaderboardPanel(overlay);
    const lbBtn = document.createElement('button');
    lbBtn.className = 'btn btn-nav';
    lbBtn.style.top = '176px';
    lbBtn.textContent = '🏆 Top 20';
    lbBtn.addEventListener('click', () => this.leaderboardPanel.toggle());
    overlay.appendChild(lbBtn);

    // Shop (with daily deals)
    let shopBuying = false;
    this.shopPanel = new ShopPanel(overlay, this.dailyShop, (cosmeticId, gemPrice) => {
      if (shopBuying) return;
      shopBuying = true;
      if (this.gems >= gemPrice && this.cosmetics.grant(cosmeticId)) {
        this.gems -= gemPrice;
      }
      this.shopPanel.update(this.gems, this.cosmetics.getOwned());
      shopBuying = false;
    });
    const shopBtn = document.createElement('button');
    shopBtn.className = 'btn btn-nav shop';
    shopBtn.style.top = '214px';
    shopBtn.textContent = '💎 Shop';
    shopBtn.addEventListener('click', () => {
      this.shopPanel.update(this.gems, this.cosmetics.getOwned());
      this.shopPanel.toggle();
    });
    overlay.appendChild(shopBtn);

    // Events
    this.eventPopup = new EventPopup(overlay, () => {
      this.eventManager.activate();
      this.trackAchievement('events_activated', 1);
      this.battlePass.addXP(this.xpManager.getXPForAction('event_activate'));
    });

    // Achievements
    this.achievementToast = new AchievementToast(overlay);
    this.achievementPanel = new AchievementPanel(overlay);

    const achieveBtn = document.createElement('button');
    achieveBtn.className = 'btn btn-nav';
    achieveBtn.style.top = '252px';
    achieveBtn.textContent = '🏆 Achieve';
    achieveBtn.addEventListener('click', () => this.achievementPanel.toggle());
    overlay.appendChild(achieveBtn);

    // Talents
    this.talentPanel = new TalentPanel(overlay, this.talents, () => this.applyTalentBonuses());

    const talentBtn = document.createElement('button');
    talentBtn.className = 'btn btn-nav';
    talentBtn.style.top = '290px';
    talentBtn.textContent = '\u2728 Talents';
    talentBtn.addEventListener('click', () => {
      this.talentPanel.refresh();
      this.talentPanel.toggle();
    });
    overlay.appendChild(talentBtn);

    // Cosmetics
    this.cosmeticsPanel = new CosmeticsPanel(overlay, this.cosmetics, (id) => {
      this.cosmetics.equip(id);
    });

    const cosmeticsBtn = document.createElement('button');
    cosmeticsBtn.className = 'btn btn-nav';
    cosmeticsBtn.style.top = '328px';
    cosmeticsBtn.textContent = '🎨 Style';
    cosmeticsBtn.addEventListener('click', () => this.cosmeticsPanel.toggle());
    overlay.appendChild(cosmeticsBtn);

    // Battle Pass
    this.battlePassPanel = new BattlePassPanel(
      overlay,
      () => {
        // Premium upgrade — TODO: wire to Stripe checkout Edge Function
        // For now, show alert instead of granting free premium
        alert('Premium upgrade coming soon! ($4.99)');
      },
      (tier) => {
        // Claim free reward
        const reward = this.battlePass.claimFreeReward(tier);
        if (reward) {
          if (reward.type === 'gems') this.gems += reward.value as number;
          if (reward.type === 'resources') {
            const amount = reward.value as number;
            this.resources.add({ gold: amount, food: amount, faith: 0, knowledge: 0 });
          }
          if (reward.type === 'cosmetic') this.cosmetics.grant(reward.value as string);
          this.battlePassPanel.update(this.battlePass);
        }
      },
      (tier) => {
        // Claim premium reward (cosmetic only)
        const cosmeticId = this.battlePass.claimPremiumReward(tier);
        if (cosmeticId) {
          this.cosmetics.grant(cosmeticId);
          this.battlePassPanel.update(this.battlePass);
        }
      },
    );

    const passBtn = document.createElement('button');
    passBtn.className = 'btn btn-nav';
    passBtn.style.top = '366px';
    passBtn.textContent = '📋 Pass';
    passBtn.addEventListener('click', () => {
      this.battlePassPanel.update(this.battlePass);
      this.battlePassPanel.toggle();
    });
    overlay.appendChild(passBtn);

    // Challenges
    this.challengePanel = new ChallengePanel(
      overlay,
      async (id) => {
        await this.challenges.acceptChallenge(id);
        const challenges = await this.challenges.loadChallenges();
        this.challengePanel.update(challenges);
      },
      async (id) => {
        // Decline = just ignore (challenge expires after 24h)
        const challenges = await this.challenges.loadChallenges();
        this.challengePanel.update(challenges);
      },
      async (targetId, objective) => {
        await this.challenges.createChallenge(targetId, objective);
        const challenges = await this.challenges.loadChallenges();
        this.challengePanel.update(challenges);
      },
    );

    const challengeBtn = document.createElement('button');
    challengeBtn.className = 'btn btn-nav';
    challengeBtn.style.top = '404px';
    challengeBtn.textContent = '⚔ Defy';
    challengeBtn.addEventListener('click', async () => {
      const challenges = await this.challenges.loadChallenges();
      this.challengePanel.update(challenges);
      this.challengePanel.toggle();
    });
    overlay.appendChild(challengeBtn);

    // Season
    this.seasonPanel = new SeasonPanel(overlay);
    this.seasonManager.loadCurrentSeason();
    this.seasonManager.loadPlayerRank(this.saveManager['playerId'] ?? '');

    const seasonBtn = document.createElement('button');
    seasonBtn.className = 'btn btn-nav';
    seasonBtn.style.top = '442px';
    seasonBtn.textContent = '🏅 Season';
    seasonBtn.addEventListener('click', () => {
      this.seasonPanel.update(this.seasonManager);
      this.seasonPanel.toggle();
    });
    overlay.appendChild(seasonBtn);

    // Profile
    this.profilePanel = new ProfilePanel(overlay, () => {
      this.profilePanel.hide();
      this.router.navigate('/');
    });

    // Router
    this.router = new Router();
    this.router.on('/', () => {
      this.profilePanel.hide();
    });
    this.router.on('/profile/:userId', (params) => {
      this.profilePanel.loadProfile(params.userId!);
    });
    this.router.resolve();

    this.saveManager.startAutoSave(() => this.buildPlayerState(), 30000);

    // Tutorial for new players
    if (this.isNewPlayer) {
      this.tutorial = new Tutorial(overlay, () => {
        this.tutorial = null;
      });
    }

    this.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
      this.cameras.main.setSize(gameSize.width, gameSize.height);
    });
  }

  update(_time: number, delta: number): void {
    const dt = delta / 1000;
    this.eventManager.update(dt);
    this.tickAccumulator += dt;
    if (this.tickAccumulator >= 1) {
      const seconds = Math.floor(this.tickAccumulator);
      this.gameLoop.tick(seconds);
      const rates = this.gameLoop.getState().productionRates;
      this.trackAchievement('gold_produced', rates.gold * seconds);
      this.trackAchievement('food_produced', rates.food * seconds);
      this.trackAchievement('faith_produced', rates.faith * seconds);
      this.trackAchievement('knowledge_produced', rates.knowledge * seconds);
      this.tickAccumulator -= seconds;
    }

    // Throttle DOM updates to ~4fps (every 250ms)
    this.uiUpdateAccumulator += dt;
    if (this.uiUpdateAccumulator >= 0.25) {
      this.uiUpdateAccumulator = 0;
      const state = this.gameLoop.getState();
      const activeEvent = this.eventManager.getActiveEvent();
      const nameColor = this.cosmetics.getEquippedValue('name_color');
      const badge = this.cosmetics.getEquippedValue('badge');
      this.hud.update(state.resources, this.gems, this.prestige.getCurrentEra().id, state.totalProductionRate, activeEvent?.type.name, nameColor, badge);
      this.buildingPanel.update(this.buildings, this.resources);
      this.buildingVisuals.update(this.buildings.getLevels());
      this.prestigePanel.update(this.prestige, this.buildings);
      const pending = this.eventManager.getPendingEvent();
      const active = this.eventManager.getActiveEvent();
      if (pending) {
        this.eventPopup.showPending(pending);
      } else if (active) {
        this.eventPopup.showActive(active);
      } else {
        this.eventPopup.hide();
      }
      this.achievementPanel.update(this.achievements);
      this.cosmeticsPanel.update(this.cosmetics);
      this.battlePassPanel.update(this.battlePass);
    }
  }

  private trackAchievement(counter: string, amount: number): void {
    const newUnlocks = this.achievements.increment(counter, amount);
    for (const a of newUnlocks) {
      const gemBonus = Math.floor(this.talents.getTalentEffect('gem_bonus'));
      this.gems += a.reward + gemBonus;
      this.achievementToast?.show(a);
      this.battlePass.addXP(this.xpManager.getXPForAction('achievement_unlock'));
    }

    // Track building upgrades for battle pass XP (every 10 upgrades)
    if (counter === 'buildings_upgraded') {
      this.upgradeCounter += amount;
      while (this.upgradeCounter >= 10) {
        this.upgradeCounter -= 10;
        this.battlePass.addXP(this.xpManager.getXPForAction('building_upgrade_10'));
      }
    }
  }

  private buildPlayerState(): PlayerState {
    return {
      id: '',
      username: '',
      era: this.prestige.getCurrentEra().id,
      prestigeCount: this.prestige.getPrestigeCount(),
      prestigeMultiplier: this.prestige.getPrestigeMultiplier(),
      gems: this.gems,
      resources: this.resources.getAll(),
      buildings: this.buildings.getLevels(),
      productionRate: this.buildings.getTotalProductionRate() * this.prestige.getPrestigeMultiplier(),
      lastTick: Date.now(),
      streakCount: this.streakCount,
      lastLoginDate: new Date().toISOString().split('T')[0]!,
      achievementCounters: this.achievements.getCounters(),
      unlockedAchievements: this.achievements.getUnlocked(),
      arcaneShards: this.talents.getShards(),
      talentLevels: this.talents.getLevels(),
      cosmeticsOwned: this.cosmetics.getOwned(),
      cosmeticsEquipped: this.cosmetics.getEquipped(),
      battlePass: this.battlePass.getState(),
    };
  }

  shutdown(): void {
    this.saveManager.stopAutoSave();
    this.saveManager.save(this.buildPlayerState());
    this.hud.destroy();
    this.buildingPanel.destroy();
    this.prestigePanel.destroy();
    this.raidPanel.destroy();
    this.leaderboardPanel.destroy();
    this.shopPanel.destroy();
    this.buildingVisuals.destroy();
    this.eventPopup.destroy();
    this.achievementPanel.destroy();
    this.achievementToast.destroy();
    this.talentPanel.destroy();
    this.cosmeticsPanel.destroy();
    this.battlePassPanel.destroy();
    this.profilePanel.destroy();
    this.challengePanel.destroy();
    this.seasonPanel.destroy();
    this.router.destroy();
  }
}
